import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronDown, ChevronRight, Check, Circle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { fetchObiettivo, fetchKrRevisioni, toggleAzione, toggleMit, toggleTargetPeriodo, patchKrStato, applyOperations } from '../lib/api';
import PageSkeleton from './nav/PageSkeleton';
import { playCheckSound } from '../lib/sound';
import { AREE } from '../constants/areas';
import { formatSettimanaConRange } from '../lib/weekFormat';
import { useUrlFilters } from '../lib/useUrlFilters';
import Breadcrumb from './nav/Breadcrumb';
import StatusBadge from './nav/StatusBadge';
import Sparkline from './nav/Sparkline';
import Checkbox from './nav/Checkbox';
import FilterBar from './nav/FilterBar';
import Tooltip from './nav/Tooltip';
import { formulaForCell } from '../lib/formula';

const FADE_TRANS = 'opacity 280ms cubic-bezier(0.22, 1, 0.36, 1)';

const fmtPad = (n) => String(n).padStart(2, '0');
const fmtNum = (v, unita) => {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  const s = Number.isInteger(n) ? n.toLocaleString('it-IT') : n.toLocaleString('it-IT', { maximumFractionDigits: 2 });
  return unita ? `${s} ${unita}` : s;
};

export default function PaginaObiettivo() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({}); // { krId: bool }
  // Filtri trasversali (stato + confidence) persistiti nell'URL.
  // L'area è univoca per obiettivo, quindi non filtra qui (hide).
  const [filters, setFilters] = useUrlFilters();

  const load = async () => {
    setLoading(true);
    try {
      const d = await fetchObiettivo(id);
      setData(d);
    } catch (e) {
      toast.error('Errore caricamento obiettivo');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  if (loading) {
    return <PageSkeleton variant="list" sections={3} rowsPerSection={4} testid="obiettivo-skeleton" />;
  }
  if (!data) return null;

  const ob = data.obiettivo;
  const area = (ob.area || '').toLowerCase();
  const meta = AREE[area] || { color: 'var(--qp-ink)', label: '—' };
  const krsAll = data.key_results || [];

  // Bucket di confidence helper
  const confidenceBucket = (c) => {
    if (c === null || c === undefined) return null;
    const n = Number(c);
    return n >= 0.7 ? 'alta' : n >= 0.4 ? 'media' : 'bassa';
  };

  // Un KR passa i filtri (stato + confidence). L'area è hidden in questa pagina.
  const krPasses = (kr) => {
    if (filters.confidence.length > 0) {
      const b = confidenceBucket(kr.confidence);
      if (!b || !filters.confidence.includes(b)) return false;
    }
    if (filters.stato.length > 0) {
      const isNumerico = kr.tipo === 'numerico';
      const isRaggiunto = isNumerico
        ? (kr.target_committed != null && kr.valore_attuale != null && Number(kr.valore_attuale) >= Number(kr.target_committed))
        : kr.stato === 'raggiunto';
      const b = confidenceBucket(kr.confidence);
      const isARischio = !isRaggiunto && b === 'bassa';
      const isOnTrack = !isRaggiunto && !isARischio;
      const match = filters.stato.some((s) => (
        (s === 'raggiunto' && isRaggiunto) ||
        (s === 'a_rischio' && isARischio) ||
        (s === 'on_track' && isOnTrack)
      ));
      if (!match) return false;
    }
    return true;
  };

  const krs = krsAll.filter(krPasses);
  const filtersActive = filters.stato.length + filters.confidence.length > 0;

  // Optimistic helpers — aggiornano data locale senza re-fetch
  const patchKr = (krId, patch) => {
    setData((prev) => prev && {
      ...prev,
      key_results: prev.key_results.map((k) => k.id === krId ? { ...k, ...patch } : k),
    });
  };
  const patchAzione = (krId, azId, patch) => {
    setData((prev) => prev && {
      ...prev,
      key_results: prev.key_results.map((k) => k.id === krId
        ? { ...k, azioni: (k.azioni || []).map((a) => a.id === azId ? { ...a, ...patch } : a) }
        : k),
    });
  };
  const patchMit = (krId, mitId, patch) => {
    setData((prev) => prev && {
      ...prev,
      key_results: prev.key_results.map((k) => k.id === krId
        ? { ...k, mit: (k.mit || []).map((m) => m.id === mitId ? { ...m, ...patch } : m) }
        : k),
    });
  };
  const patchCell = (krId, gran, etichetta, patch) => {
    setData((prev) => prev && {
      ...prev,
      key_results: prev.key_results.map((k) => {
        if (k.id !== krId) return k;
        const arr = k[gran] || [];
        return { ...k, [gran]: arr.map((c) => c.etichetta === etichetta ? { ...c, ...patch } : c) };
      }),
    });
  };

  const handleToggleKr = async (kr) => {
    if (kr.tipo === 'numerico') return;
    const willBe = kr.stato === 'raggiunto' ? 'attivo' : 'raggiunto';
    const prev = kr.stato;
    patchKr(kr.id, { stato: willBe });
    if (willBe === 'raggiunto') playCheckSound();
    try {
      await patchKrStato(kr.id, willBe);
    } catch {
      patchKr(kr.id, { stato: prev });
      toast.error('Salvataggio KR fallito');
    }
  };

  const handleToggleAzione = async (krId, az, next) => {
    patchAzione(krId, az.id, { fatto: next });
    if (next && !az.fatto) playCheckSound();
    try { await toggleAzione(az.id, next); }
    catch { patchAzione(krId, az.id, { fatto: az.fatto }); toast.error('Salvataggio azione fallito'); }
  };

  const handleToggleMit = async (krId, m, next) => {
    patchMit(krId, m.id, { fatto: next });
    if (next && !m.fatto) playCheckSound();
    try { await toggleMit(m.id, next); }
    catch { patchMit(krId, m.id, { fatto: m.fatto }); toast.error('Salvataggio MIT fallito'); }
  };

  const handleToggleCell = async (krId, gran, cell, next) => {
    if (!cell.target_periodo_id) return;
    patchCell(krId, gran, cell.etichetta, { raggiunto: next });
    if (next && !cell.raggiunto) playCheckSound();
    try { await toggleTargetPeriodo(cell.target_periodo_id, next); }
    catch { patchCell(krId, gran, cell.etichetta, { raggiunto: cell.raggiunto }); toast.error('Salvataggio cella fallito'); }
  };

  const handleToggleRipartizione = async (kr) => {
    const next = (kr.ripartizione || 'lineare') === 'manuale' ? 'lineare' : 'manuale';
    const prev = kr.ripartizione || 'lineare';
    patchKr(kr.id, { ripartizione: next });
    try {
      await applyOperations([{
        entita: 'key_result',
        azione: 'aggiorna',
        id: kr.id,
        campi: { ripartizione: next },
      }]);
      toast.success(next === 'manuale'
        ? 'KR ora a tappe — niente proiezione lineare automatica'
        : 'KR ora lineare — proiezione mensile automatica');
    } catch {
      patchKr(kr.id, { ripartizione: prev });
      toast.error('Salvataggio ripartizione fallito');
    }
  };

  return (
    <div className="min-h-screen bg-canvas text-ink">
      <div className="max-w-7xl mx-auto px-6 pt-2">
        <Breadcrumb
          backTo="/"
          backLabel="Dashboard"
          items={[
            { label: 'Dashboard', to: '/' },
            { label: `Obiettivo ${fmtPad(ob.ordine)}` },
          ]}
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 pb-20">
        {/* HEADER */}
        <header className="border-b border-line pb-8 mb-10" data-testid="obiettivo-header">
          <div className="flex items-baseline gap-4 mb-3">
            <span className="font-mono text-sm" style={{ color: meta.color }}>{fmtPad(ob.ordine)}</span>
            <span className="font-mono text-xs uppercase tracking-[0.2em]" style={{ color: meta.color }}>{meta.label}</span>
            {ob.stato && ob.stato !== 'attivo' && (
              <span className="font-mono text-xs uppercase tracking-[0.15em] text-mute">· {ob.stato}</span>
            )}
          </div>
          <h1 className="font-display tracking-tight text-3xl md:text-4xl lg:text-5xl mb-3" style={{ color: meta.color }}>
            {ob.titolo}
          </h1>
          {ob.perche && (
            <p className="font-body text-base text-soft leading-relaxed max-w-3xl">{ob.perche}</p>
          )}
          <div className="mt-6 font-mono text-xs text-soft">
            {filtersActive
              ? <>{krs.length} di {krsAll.length} key result · ad anno {data.anno?.anno}</>
              : <>{krsAll.length} key result · ad anno {data.anno?.anno}</>
            }
          </div>
        </header>

        {/* Filtri trasversali (area nascosta: univoca per obiettivo) */}
        <FilterBar filters={filters} onChange={setFilters} hide={['area']} />

        {/* KR LIST */}
        <section className="space-y-4">
          {krs.length === 0 ? (
            <div className="border border-dashed border-line p-10 text-center font-body text-sm text-mute" data-testid="obiettivo-empty">
              {filtersActive
                ? 'Nessun KR soddisfa i filtri attivi. Pulisci i filtri per vedere tutti i KR.'
                : 'Nessun key result definito per questo obiettivo.'}
            </div>
          ) : krs.map((kr) => (
            <KrCard
              key={kr.id}
              kr={kr}
              area={area}
              meta={meta}
              anno={data.anno?.anno}
              expanded={!!expanded[kr.id]}
              onToggleExpand={() => setExpanded((m) => ({ ...m, [kr.id]: !m[kr.id] }))}
              onToggleKr={() => handleToggleKr(kr)}
              onToggleAzione={(az, next) => handleToggleAzione(kr.id, az, next)}
              onToggleMit={(m, next) => handleToggleMit(kr.id, m, next)}
              onToggleCell={(gran, c, next) => handleToggleCell(kr.id, gran, c, next)}
              onToggleRipartizione={() => handleToggleRipartizione(kr)}
            />
          ))}
        </section>
      </div>
    </div>
  );
}

function KrCard({ kr, area, meta, anno, expanded, onToggleExpand, onToggleKr, onToggleAzione, onToggleMit, onToggleCell, onToggleRipartizione }) {
  const isNumerico = kr.tipo === 'numerico';
  const isManuale = (kr.ripartizione || 'lineare') === 'manuale';
  const raggiunto = isNumerico
    ? (kr.target_committed != null && kr.valore_attuale != null && Number(kr.valore_attuale) >= Number(kr.target_committed))
    : kr.stato === 'raggiunto';

  return (
    <div className="border border-line" data-testid={`kr-card-${kr.id}`}>
      <div className="p-5 flex items-start gap-4">
        {/* Check KR */}
        <Checkbox
          checked={raggiunto}
          onToggle={onToggleKr}
          size={20}
          disabled={isNumerico}
          title={isNumerico ? 'KR numerico — automatico da valore reale vs target' : (raggiunto ? 'Riapri' : 'Segna raggiunto')}
          label={kr.titolo}
          testid={`kr-toggle-${kr.id}`}
        />

        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-3 flex-wrap mb-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-mute px-1.5 py-0.5 border border-line">
              {kr.natura || kr.tipo}
            </span>
            {isNumerico && (
              <Tooltip
                label={isManuale
                  ? 'KR a tappe: niente proiezione lineare automatica. Lo Scostamento usa solo i target_periodo espliciti. Clicca per tornare a lineare.'
                  : 'KR lineare: il sistema deriva automaticamente target mensili/trimestrali da baseline a target. Clicca per passare a tappe (utile per numeri piccoli/discreti come "2 clienti l\'anno").'}
                side="top"
                wide
                maxWidth={320}
                delay={200}
              >
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onToggleRipartizione && onToggleRipartizione(); }}
                  className={`font-mono text-[10px] uppercase tracking-[0.18em] px-1.5 py-0.5 border transition-colors ${
                    isManuale
                      ? 'border-ink bg-ink text-canvas hover:bg-ink/80'
                      : 'border-line text-mute hover:border-ink hover:text-ink'
                  }`}
                  data-testid={`kr-ripartizione-${kr.id}`}
                  aria-label={isManuale ? 'Ripartizione manuale (a tappe) — clicca per lineare' : 'Ripartizione lineare — clicca per a tappe'}
                >
                  {isManuale ? 'a tappe' : 'lineare'}
                </button>
              </Tooltip>
            )}
            <h3
              className="font-display text-lg md:text-xl tracking-tight flex-1"
              style={{
                textDecoration: raggiunto ? 'line-through' : 'none',
                opacity: raggiunto ? 0.5 : 1,
                transition: FADE_TRANS,
              }}
            >{kr.titolo}</h3>
            {isNumerico && (
              <span className="font-mono text-sm tabular-nums text-soft">
                {fmtNum(kr.valore_attuale, kr.unita)} / {fmtNum(kr.target_committed, kr.unita)}
              </span>
            )}
          </div>

          {/* Backstop badge — contro-metrica di guardia */}
          {kr.backstop_kr && (
            <Tooltip
              label={`Backstop: questa contro-metrica NON deve peggiorare mentre persegui il KR principale.\n\n${kr.backstop_kr.titolo}\nAttuale: ${fmtNum(kr.backstop_kr.valore_attuale, kr.backstop_kr.unita)}${kr.backstop_kr.target_committed != null ? ` · Soglia: ${fmtNum(kr.backstop_kr.target_committed, kr.backstop_kr.unita)}` : ''}`}
              side="top"
              wide
              maxWidth={300}
              delay={200}
            >
              <div
                className="inline-flex items-baseline gap-2 mb-3 px-2 py-1 border border-[#D97706]/40 bg-[#FFFBEB] cursor-help"
                data-testid={`kr-backstop-${kr.id}`}
              >
                <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#D97706]"><AlertTriangle className="inline h-2.5 w-2.5 align-[-1px]" strokeWidth={1.5} /> Backstop</span>
                <span className="font-body text-xs text-ink">{kr.backstop_kr.titolo}</span>
                {kr.backstop_kr.valore_attuale != null && (
                  <span className="font-mono text-[11px] tabular-nums text-soft">
                    {fmtNum(kr.backstop_kr.valore_attuale, kr.backstop_kr.unita)}
                  </span>
                )}
              </div>
            </Tooltip>
          )}

          {/* Progress bar (numerici) — con sparkline traiettoria reale vs suggerita */}
          {isNumerico && kr.progress_pct != null && (
            <div className="flex items-center gap-3 mb-3">
              <div className="flex-1 h-[3px] bg-linesoft relative overflow-hidden">
                <div
                  className="h-full transition-[width] duration-700 ease-out"
                  style={{ width: `${Math.min(100, kr.progress_pct)}%`, backgroundColor: meta.color }}
                />
              </div>
              <span className="font-mono text-xs tabular-nums text-soft min-w-[40px] text-right">
                {Math.round(kr.progress_pct)}%
              </span>
              <KrTrendSpark kr={kr} color={meta.color} />
            </div>
          )}

          {/* Quarter row */}
          <div className="flex items-center gap-2 mt-3 overflow-x-auto" data-testid={`kr-quarters-${kr.id}`}>
            {(kr.quarters || []).map((q) => (
              <Cell
                key={q.etichetta}
                etichetta={q.etichetta.replace(`-${anno}`, '')}
                suggerito={q.suggerito}
                impegnato={q.impegnato}
                reale={q.reale}
                raggiunto={q.raggiunto}
                onTrack={q.on_track}
                unita={kr.unita}
                isNumerico={isNumerico}
                tpId={q.target_periodo_id}
                periodoLabel={q.etichetta}
                granularita="quarter"
                krId={kr.id}
                areaColor={meta.color}
                kr={kr}
                quartersAll={kr.quarters}
                mesiAll={kr.mesi}
                onChanged={undefined}
                onToggleCell={(c, next) => onToggleCell && onToggleCell('quarters', c, next)}
              />
            ))}
          </div>

          {/* Expand button */}
          <button
            onClick={onToggleExpand}
            className="mt-3 inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.18em] text-soft hover:text-ink"
            data-testid={`kr-expand-${kr.id}`}
          >
            {expanded
              ? <><ChevronDown size={12} strokeWidth={1.5} /> Riduci timeline</>
              : <><ChevronRight size={12} strokeWidth={1.5} /> Mostra mesi · settimane · azioni</>
            }
          </button>

          {expanded && (
            <div className="mt-4 pt-4 border-t border-linesoft space-y-5">
              {/* Mesi */}
              <div>
                <div className="font-mono text-xs uppercase tracking-[0.18em] text-mute mb-2">Mesi · {anno}</div>
                <div className="grid grid-cols-6 md:grid-cols-12 gap-1.5">
                  {(kr.mesi || []).map((m) => (
                    <Cell
                      key={m.etichetta}
                      etichetta={m.mese_nome}
                      suggerito={m.suggerito}
                      impegnato={m.impegnato}
                      reale={m.reale}
                      raggiunto={m.raggiunto}
                      onTrack={m.on_track}
                      unita={kr.unita}
                      isNumerico={isNumerico}
                      tpId={m.target_periodo_id}
                      periodoLabel={m.etichetta}
                      granularita="mese"
                      krId={kr.id}
                      areaColor={meta.color}
                      kr={kr}
                      quartersAll={kr.quarters}
                      mesiAll={kr.mesi}
                      onToggleCell={(c, next) => onToggleCell && onToggleCell('mesi', c, next)}
                      compact
                    />
                  ))}
                </div>
              </div>

              {/* Settimane */}
              {(kr.settimane || []).length > 0 && (
                <div>
                  <div className="font-mono text-xs uppercase tracking-[0.18em] text-mute mb-2">Settimane</div>
                  <div className="flex flex-wrap gap-1.5">
                    {kr.settimane.map((w) => (
                      <Cell
                        key={w.etichetta}
                        etichetta={w.etichetta.replace(`${anno}-`, '')}
                        suggerito={w.suggerito}
                        impegnato={w.impegnato}
                        reale={w.reale}
                        raggiunto={w.raggiunto}
                        onTrack={w.on_track}
                        unita={kr.unita}
                        isNumerico={isNumerico}
                        tpId={w.target_periodo_id}
                        periodoLabel={w.etichetta}
                        granularita="settimana"
                        krId={kr.id}
                        areaColor={meta.color}
                        kr={kr}
                        quartersAll={kr.quarters}
                        mesiAll={kr.mesi}
                        onToggleCell={(c, next) => onToggleCell && onToggleCell('settimane', c, next)}
                        compact
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Azioni */}
              {(kr.azioni || []).length > 0 && (
                <div>
                  <div className="font-mono text-xs uppercase tracking-[0.18em] text-mute mb-2">
                    Azioni collegate · {kr.azioni.length}
                  </div>
                  <ul className="divide-y divide-linesoft" data-testid={`kr-azioni-${kr.id}`}>
                    {kr.azioni.map((a) => (
                      <ToggleRow
                        key={a.id}
                        kind="azione"
                        item={a}
                        text={a.testo}
                        meta={formatSettimanaConRange(a.settimana)}
                        onToggle={(next) => onToggleAzione(a, next)}
                      />
                    ))}
                  </ul>
                </div>
              )}

              {/* MIT */}
              {(kr.mit || []).length > 0 && (
                <div>
                  <div className="font-mono text-xs uppercase tracking-[0.18em] text-mute mb-2">
                    Most Important Task (MIT) collegate · {kr.mit.length}
                  </div>
                  <ul className="divide-y divide-linesoft" data-testid={`kr-mit-${kr.id}`}>
                    {kr.mit.map((m) => (
                      <ToggleRow
                        key={m.id}
                        kind="mit"
                        item={m}
                        text={m.testo}
                        meta={m.data}
                        onToggle={(next) => onToggleMit(m, next)}
                      />
                    ))}
                  </ul>
                </div>
              )}

              {/* Storico modifiche (tabella revisioni, scritta dal coach) */}
              <KrRevisioni krId={kr.id} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * KrRevisioni — "Storico modifiche" collassabile per KR.
 * Legge la tabella `revisioni` via GET /api/kr/{id}/revisioni (lazy: fetch al
 * primo expand). Se non ci sono revisioni, la sezione non compare.
 */
function KrRevisioni({ krId }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null); // null = non ancora caricato

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && rows === null) {
      try {
        const res = await fetchKrRevisioni(krId);
        setRows(res.revisioni || []);
      } catch {
        setRows([]);
      }
    }
  };

  // Dopo il fetch, se vuoto, mostra solo il bottone disattivato? Meglio: niente
  // sezione se sappiamo che è vuota.
  if (rows !== null && rows.length === 0 && !open) return null;

  return (
    <div data-testid={`kr-revisioni-${krId}`}>
      <button
        onClick={toggle}
        className="inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.18em] text-soft hover:text-ink"
        data-testid={`kr-revisioni-toggle-${krId}`}
        aria-expanded={open}
      >
        {open
          ? <ChevronDown size={12} strokeWidth={1.5} />
          : <ChevronRight size={12} strokeWidth={1.5} />}
        Storico modifiche
      </button>
      {open && (
        rows === null ? (
          <p className="font-body text-xs text-mute mt-2">Carico…</p>
        ) : rows.length === 0 ? (
          <p className="font-body text-xs text-mute mt-2 italic">Nessuna revisione registrata per questo KR.</p>
        ) : (
          <ul className="mt-2 divide-y divide-linesoft border-t border-linesoft" data-testid={`kr-revisioni-list-${krId}`}>
            {rows.map((r) => (
              <li key={r.id} className="py-2 flex items-baseline gap-3 flex-wrap">
                <span className="font-mono text-[10px] tabular-nums text-mute shrink-0">{r.data}</span>
                {r.campo && (
                  <span className="font-mono text-[10px] uppercase tracking-[0.15em] border border-line px-1 py-0.5 text-soft shrink-0">{r.campo}</span>
                )}
                {(r.valore_precedente != null || r.valore_nuovo != null) && (
                  <span className="font-mono text-xs tabular-nums text-soft shrink-0">
                    {r.valore_precedente ?? '—'} → {r.valore_nuovo ?? '—'}
                  </span>
                )}
                <span className="font-body text-xs text-soft">{r.motivo}</span>
              </li>
            ))}
          </ul>
        )
      )}
    </div>
  );
}

function Cell({
  etichetta, suggerito, impegnato, reale, raggiunto, onTrack, unita, isNumerico,
  tpId, periodoLabel, granularita, krId, areaColor, onToggleCell, compact = false,
  kr, quartersAll, mesiAll,
}) {
  const showVal = isNumerico ? (reale != null ? reale : suggerito) : null;
  const linkTo = `/periodo/${granularita}/${periodoLabel}?kr=${krId}`;

  const handleToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!tpId || !onToggleCell) return;
    onToggleCell({ etichetta: periodoLabel, raggiunto, target_periodo_id: tpId }, !raggiunto);
  };

  // Tooltip "Come ho ricavato questo numero?" — solo su KR numerici e quando manca il reale
  const formulaText = (isNumerico && reale == null && suggerito != null)
    ? formulaForCell({ kr, quartersAll, mesiAll, granularita, periodoLabel, suggerito })
    : null;

  const cellEl = (
    <Link
      to={linkTo}
      className={
        'group flex flex-col items-center justify-center border border-line hover:border-ink transition-colors ' +
        (compact ? 'px-1.5 py-1.5 min-w-[58px]' : 'px-3 py-2 min-w-[88px]')
      }
      data-testid={`cell-${granularita}-${periodoLabel}-${krId}`}
      title={formulaText ? undefined : `${granularita} ${periodoLabel} · ${onTrack || 'n.d.'}`}
      style={{ opacity: (raggiunto && !isNumerico) ? 0.65 : 1, transition: FADE_TRANS }}
    >
      <div className="flex items-center gap-1.5">
        <StatusBadge stato={raggiunto && !isNumerico ? 'raggiunto' : onTrack} size={compact ? 'sm' : 'md'} />
        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-soft">{etichetta}</span>
      </div>
      {isNumerico ? (
        <span
          className={'font-mono text-xs tabular-nums mt-0.5 ' + (formulaText ? 'border-b border-dotted border-mute' : '')}
          style={{ color: areaColor }}
        >
          {fmtNum(showVal, unita)}
        </span>
      ) : (
        tpId && (
          <button
            onClick={handleToggle}
            className="mt-0.5 text-[10px] font-mono uppercase tracking-[0.15em] text-soft hover:text-ink"
            data-testid={`cell-toggle-${tpId}`}
          >
            {raggiunto ? (
              <><Check className="inline h-3 w-3 align-[-2px]" strokeWidth={1.5} /> fatto</>
            ) : (
              <><Circle className="inline h-3 w-3 align-[-2px]" strokeWidth={1.5} /> aperto</>
            )}
          </button>
        )
      )}
    </Link>
  );

  if (!formulaText) return cellEl;
  return (
    <Tooltip label={formulaText} side="top" wide delay={250} maxWidth={320}>
      {cellEl}
    </Tooltip>
  );
}

/**
 * Mini-grafico KR numerico: traiettoria suggerita (linea pallida) vs reale
 * (linea piena). Usa i 12 dati mensili (kr.mesi[i].suggerito/reale).
 * Se non ci sono `reale` ancora, cade su una sparkline della sola suggerita.
 */
function KrTrendSpark({ kr, color = 'var(--qp-ink)' }) {
  const mesi = kr.mesi || [];
  const sugg = mesi.map((m) => (m.suggerito != null ? Number(m.suggerito) : null));
  const real = mesi.map((m) => (m.reale != null ? Number(m.reale) : null));
  const allVals = [...sugg, ...real].filter((v) => v != null);
  if (allVals.length === 0) return null;
  const min = Math.min(...allVals);
  const max = Math.max(...allVals);
  const range = max - min || 1;
  const W = 90, H = 22, PAD = 1;
  const xStep = (W - 2 * PAD) / Math.max(1, mesi.length - 1);
  const yOf = (v) => H - PAD - ((v - min) / range) * (H - 2 * PAD);

  const buildPath = (arr) => {
    let d = '';
    let firstDrawn = false;
    arr.forEach((v, i) => {
      if (v == null) return;
      const x = PAD + i * xStep;
      const y = yOf(v);
      d += firstDrawn ? ` L ${x.toFixed(1)} ${y.toFixed(1)}` : `M ${x.toFixed(1)} ${y.toFixed(1)}`;
      firstDrawn = true;
    });
    return d;
  };

  // Posizione "oggi" (mese corrente)
  const todayIdx = Math.min(11, new Date().getMonth());
  const todayX = PAD + todayIdx * xStep;

  return (
    <svg width={W} height={H} role="img" aria-label="Trend KR" data-testid={`kr-trend-${kr.id}`}>
      {/* Linea OGGI */}
      <line x1={todayX} y1={0} x2={todayX} y2={H} stroke="var(--qp-border)" strokeWidth={1} />
      {/* Suggerita — più sottile, dashed */}
      <path d={buildPath(sugg)} fill="none" stroke="var(--qp-text-mute)" strokeWidth={1} strokeDasharray="2 2" />
      {/* Reale — piena, colorata */}
      <path d={buildPath(real)} fill="none" stroke={color} strokeWidth={1.5} />
    </svg>
  );
}

function ToggleRow({ kind, item, text, meta, onToggle }) {
  return (
    <li className="py-2 flex items-center gap-3" data-testid={`toggle-row-${kind}-${item.id}`}>
      <Checkbox
        checked={item.fatto}
        onToggle={onToggle}
        size={16}
        label={text}
        testid={`toggle-btn-${kind}-${item.id}`}
      />
      <span
        className="flex-1 font-body text-sm"
        style={{
          textDecoration: item.fatto ? 'line-through' : 'none',
          color: item.fatto ? 'var(--qp-text-mute)' : undefined,
          opacity: item.fatto ? 0.5 : 1,
          transition: FADE_TRANS,
        }}
      >
        {text}
      </span>
      {meta && (
        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-mute shrink-0">{meta}</span>
      )}
    </li>
  );
}
